import type { TFunction } from 'i18next';
import { formatDuration } from '@/features/trips/format';
import type { Schedule, VehicleOdometer } from './types';

/**
 * Presentation helpers for maintenance. Pure and tested, so the sentence a
 * customer reads under an odometer is not decided inside JSX.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔴 THE ODOMETER IS NEVER RENDERED AS A BARE ABSOLUTE NUMBER.
 * ═════════════════════════════════════════════════════════════════════════════
 * The pilot's FMC920 counter is corrupt in two independent ways (a +106,352,799 m
 * single step on a fix whose own `distance` was 7 m, and seven decreases), so
 * Raad's odometer is a SUM over detected trips plus an optional human-entered
 * dashboard baseline. That is a different quantity from "the number on the dash",
 * and the difference is the customer's to see, not ours to hide.
 *
 * `VehicleOdometerZ` therefore carries its provenance as part of the CONTRACT —
 * `source`, `baselineKm`, `tripCount`, `earliestTripAt` — precisely so this file
 * can write the sentence that says where the number came from. A bare figure
 * would be a claim we cannot support (CANVAS_V2_GUARD item 1: never fabricate a
 * number, an odometer especially).
 *
 * `odometerProvenance` returns BOTH halves together for that reason. There is no
 * function here that returns the headline alone.
 *
 * NO UNIT STRING IS HARDCODED. Kilometres go through `trips.unit.km`, durations
 * through the shared `formatDuration`, so the Somali build renders Somali units.
 */

/** Grouped kilometres — "112,340 km". One decimal at most; the wire carries
 *  metres rounded to the kilometre, so a long tail would be invented precision. */
export function formatKm(t: TFunction, locale: string, km: number): string {
  return t('trips.unit.km', { value: groupNumber(locale, km, 1) });
}

/** Engine hours through the SHARED duration formatter — "64 h 9 m". Hours in,
 *  seconds to the formatter, so hours and trip durations read identically. */
export function formatEngineHours(t: TFunction, hours: number): string {
  return formatDuration(t, Math.round(hours * 3600));
}

/** Whole days, pluralised. Negative values are the caller's business — the day
 *  lines below decide "in N days" versus "N days overdue". */
export function formatDays(t: TFunction, days: number): string {
  return t('maintenance.unit.days', { count: Math.abs(Math.round(days)) });
}

function groupNumber(locale: string, value: number, maxFractionDigits: number): string {
  const opts: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFractionDigits,
  };
  try {
    return new Intl.NumberFormat(locale || 'en', opts).format(value);
  } catch {
    return new Intl.NumberFormat('en', opts).format(value);
  }
}

/**
 * A CALENDAR DAY (`YYYY-MM-DD`) rendered as a date, resolved at noon UTC.
 *
 * A due date is a DAY, not an instant — the API validates it as `YYYY-MM-DD` for
 * exactly that reason — so it must not be pushed through a timezone that could
 * roll it across midnight. Noon UTC formatted in UTC cannot move for any zone on
 * earth; using the tenant zone here would be the day-boundary defect in reverse.
 */
export function formatDay(locale: string, day: string): string {
  const ms = Date.parse(`${day}T12:00:00Z`);
  if (!Number.isFinite(ms)) return day;
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

/** An instant rendered as a date in the TENANT's zone — for a baseline reading
 *  and the earliest trip, both of which are real instants. */
export function formatInstantDay(locale: string, iso: string, timeZone: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone,
  };
  try {
    return new Intl.DateTimeFormat(locale || 'en', opts).format(ms);
  } catch {
    return new Intl.DateTimeFormat('en', opts).format(ms);
  }
}

export interface OdometerCopy {
  /** The figure the tile shows large. */
  headline: string;
  /** 🔴 ALWAYS PRESENT. The sentence saying where `headline` came from. */
  provenance: string;
}

/**
 * The odometer tile's two lines. 🔴 Both, always — there is no branch that
 * returns a headline with no provenance.
 *
 *   `baseline_plus_trips` → "112,340 km" + "dashboard reading 100,000 km on
 *                            3 Sep 2026, plus 12,340 km since"
 *   `trips_only`          → "12,340 km" + "since Raad started tracking — from 75
 *                            trips since 11 Aug 2026"
 *
 * The `trips_only` headline is NOT an absolute odometer and the sentence says so
 * in the same breath. A vehicle with no trips yet gets its own sentence rather
 * than "from 0 trips since —", which reads as a broken screen.
 */
export function odometerProvenance(
  t: TFunction,
  locale: string,
  timeZone: string,
  odo: VehicleOdometer,
): OdometerCopy {
  const headline = formatKm(t, locale, odo.odometerKm);
  if (odo.source === 'baseline_plus_trips' && odo.baselineKm !== null && odo.baselineAt !== null) {
    return {
      headline,
      provenance: t('maintenance.odometer.fromBaseline', {
        baseline: formatKm(t, locale, odo.baselineKm),
        date: formatInstantDay(locale, odo.baselineAt, timeZone),
        since: formatKm(t, locale, odo.tripDistanceKm),
      }),
    };
  }
  if (odo.tripCount === 0 || odo.earliestTripAt === null) {
    return { headline, provenance: t('maintenance.odometer.noTrips') };
  }
  return {
    headline,
    provenance: t('maintenance.odometer.fromTrips', {
      count: odo.tripCount,
      date: formatInstantDay(locale, odo.earliestTripAt, timeZone),
    }),
  };
}

/** "every 5,000 km" / "every 20 h 0 m" / "one-off" (an `on_date` schedule has no
 *  interval — it is a single occurrence that the user re-dates on completion). */
export function intervalLine(t: TFunction, locale: string, s: Schedule): string {
  if (s.trigger === 'every_km' && s.intervalKm !== null) {
    return t('maintenance.row.everyKm', { value: formatKm(t, locale, s.intervalKm) });
  }
  if (s.trigger === 'every_hours' && s.intervalHours !== null) {
    return t('maintenance.row.everyHours', { value: formatEngineHours(t, s.intervalHours) });
  }
  return t('maintenance.row.oneOff');
}

/**
 * The due line. `remaining*` is SERVER-COMPUTED against the same odometer the
 * evaluator reads (seam S1), so the screen and the alert can never disagree —
 * this function never re-derives it from `dueKm` minus a local odometer.
 *
 * Null `remaining` means the measure was unavailable (no engine-hours counter, no
 * resolvable tenant day). The line then states the target WITHOUT a distance to
 * it, rather than printing a zero that would read as "due exactly now".
 */
export function dueLine(t: TFunction, locale: string, s: Schedule): string {
  if (s.trigger === 'every_km') {
    if (s.dueKm === null) return t('maintenance.row.dueUnknown');
    const due = formatKm(t, locale, s.dueKm);
    if (s.remainingKm === null) return t('maintenance.row.dueAtOnly', { due });
    if (s.remainingKm < 0) {
      return t('maintenance.row.overdueBy', { value: formatKm(t, locale, -s.remainingKm) });
    }
    return t('maintenance.row.dueAt', { due, remaining: formatKm(t, locale, s.remainingKm) });
  }
  if (s.trigger === 'every_hours') {
    if (s.dueHours === null) return t('maintenance.row.dueUnknown');
    const due = formatEngineHours(t, s.dueHours);
    if (s.remainingHours === null) return t('maintenance.row.dueAtOnly', { due });
    if (s.remainingHours < 0) {
      return t('maintenance.row.overdueBy', { value: formatEngineHours(t, -s.remainingHours) });
    }
    return t('maintenance.row.dueAt', { due, remaining: formatEngineHours(t, s.remainingHours) });
  }
  if (s.dueOn === null) return t('maintenance.row.dueUnknown');
  const date = formatDay(locale, s.dueOn);
  if (s.remainingDays === null) return t('maintenance.row.dueOnOnly', { date });
  if (s.remainingDays < 0) {
    return t('maintenance.row.overdueByDays', { value: formatDays(t, s.remainingDays) });
  }
  if (s.remainingDays === 0) return t('maintenance.row.dueToday', { date });
  return t('maintenance.row.dueOn', { date, remaining: formatDays(t, s.remainingDays) });
}

/** "Last done 12 Aug 2026 at 9,800 km" / "Last done 12 Aug 2026" / "Never done". */
export function lastDoneLine(
  t: TFunction,
  locale: string,
  timeZone: string,
  s: Schedule,
): string {
  if (s.lastDoneAt === null) return t('maintenance.row.neverDone');
  const date = formatInstantDay(locale, s.lastDoneAt, timeZone);
  if (s.trigger === 'every_km' && s.lastDoneKm !== null) {
    return t('maintenance.row.lastDoneAt', { date, value: formatKm(t, locale, s.lastDoneKm) });
  }
  if (s.trigger === 'every_hours' && s.lastDoneHours !== null) {
    return t('maintenance.row.lastDoneAt', { date, value: formatEngineHours(t, s.lastDoneHours) });
  }
  return t('maintenance.row.lastDone', { date });
}

/**
 * 🔴 THE RE-ARM RULE, MADE VISIBLE BEFORE IT IS COMMITTED.
 *
 * `due := C + interval`, where **C is the COMPLETION value, not the old due
 * value** (api `store.completeSchedule`). Re-arming from the old due would make a
 * service done 1,500 km late arrive 1,500 km early next time, and the error
 * compounds every cycle — so the mark-done dialog shows the resulting target live
 * as the user edits, rather than letting them discover it a month later.
 *
 * This mirrors the server's arithmetic exactly and is the ONLY place the web does
 * so; it is a PREVIEW, never a value sent on the wire. The client may never supply
 * a next-due — that is a route-level 422 — and this returning a number changes
 * nothing about what is POSTed.
 *
 * A supplied `odometerKm` is the customer's own dashboard figure and is used as C
 * verbatim, which is what the shipped handler does (`completionValue = reportedM`).
 * The baseline already aligns Raad's space to the dashboard, so no second
 * adjustment belongs here.
 */
export function previewNextDue(
  s: Schedule,
  measures: { odometerKm: number | null; engineHours: number | null },
  input: { odometerKm: number | null },
): { kind: 'km' | 'hours'; value: number } | null {
  if (s.trigger === 'every_km') {
    if (s.intervalKm === null) return null;
    const c = input.odometerKm ?? measures.odometerKm;
    if (c === null) return null;
    return { kind: 'km', value: c + s.intervalKm };
  }
  if (s.trigger === 'every_hours') {
    if (s.intervalHours === null || measures.engineHours === null) return null;
    return { kind: 'hours', value: measures.engineHours + s.intervalHours };
  }
  return null;
}

/** The preview rendered. Separate from the arithmetic so the arithmetic is
 *  testable without a `t`. */
export function nextDueLine(
  t: TFunction,
  locale: string,
  next: { kind: 'km' | 'hours'; value: number } | null,
): string | null {
  if (next === null) return null;
  return t('maintenance.markDone.nextDue', {
    value:
      next.kind === 'km' ? formatKm(t, locale, next.value) : formatEngineHours(t, next.value),
  });
}

/**
 * Sort for `/maintenance`: overdue first, then by urgency inside each state.
 *
 * Urgency is compared as a FRACTION OF THE NOTIFY WINDOW, not as a raw number,
 * because 200 km and 200 hours and 200 days are not comparable quantities and
 * sorting them against each other would put a distance schedule above a date one
 * for no reason a customer could read. Rows with no measure sort last — an
 * unknown is not urgent, and it must not jump the queue.
 */
export function urgencyRank(s: Schedule): number {
  const stateRank = s.state === 'overdue' ? 0 : s.state === 'due' ? 1 : 2;
  const remaining =
    s.remainingKm ?? s.remainingHours ?? (s.remainingDays === null ? null : s.remainingDays);
  const window =
    s.notifyBeforeKm ?? s.notifyBeforeHours ?? (s.notifyBeforeDays === null ? null : s.notifyBeforeDays);
  if (remaining === null) return stateRank * 1000 + 999;
  // A zero notify window is legal (`notifyBeforeKm=0`), so it can never be a
  // divisor. Fall back to the raw remaining, capped, which keeps ordering stable.
  const fraction = window && window > 0 ? remaining / window : Math.sign(remaining) * 0.5;
  return stateRank * 1000 + Math.max(-999, Math.min(998, fraction));
}

export function sortByUrgency(rows: readonly Schedule[]): Schedule[] {
  return [...rows].sort((a, b) => urgencyRank(a) - urgencyRank(b));
}
